/**
 * components/fiscal/FiscalModeBadge.js
 * ----------------------------------------------------------------------------
 * The environment a fiscal fact belongs to, and the rule that a fiscal
 * document number is never shown without it.
 *
 * WHY THIS EXISTS. URA runs a sandbox and a production system, and both issue
 * numbers that look identical. A sandbox FDN from this project has already
 * been read as a live one once. The only defence that survives a screenshot,
 * a copy-paste into WhatsApp or a tired reader is to print the environment
 * beside the number every single time, in a colour the eye reads before the
 * digits: Lusuku yellow for TEST, green for LIVE.
 *
 *   <FiscalModeBadge mode="TEST" />          the badge on its own (page header,
 *                                            a mode column)
 *   <FiscalNumber value={fdn} mode={mode} /> a number with its badge; renders
 *                                            "Not issued" plus the badge when
 *                                            there is no number yet, so the
 *                                            column never goes blank and never
 *                                            loses its environment either
 *
 * Nothing here knows about documents or rows. It is deliberately small so the
 * fiscal documents table, the health page and the vendor tax page cannot each
 * draw their own badge and drift.
 */
import React from "react";
import { TYPE } from "./fiscalUi.js";

const TONES = {
  TEST: { bg: "#F2C811", fg: "#0F172A", label: "TEST" },
  LIVE: { bg: "#e6faf7", fg: "#047857", label: "LIVE" },
};
const UNKNOWN = { bg: "#eef2f7", fg: "#51637a", label: "UNKNOWN" };

const normaliseMode = (mode) => {
  const key = String(mode || "").trim().toUpperCase();
  return TONES[key] ? key : null;
};

/**
 * @param {{ mode: string, size?: "sm"|"md", title?: string, className?: string }} props
 */
const FiscalModeBadge = ({ mode, size = "sm", title, className }) => {
  const key = normaliseMode(mode);
  const tone = key ? TONES[key] : UNKNOWN;
  const text = key ? tone.label : UNKNOWN.label;
  return (
    <span
      className={["lk-pill", "lk-fiscal-mode", `lk-fiscal-mode--${(key || "unknown").toLowerCase()}`, className]
        .filter(Boolean)
        .join(" ")}
      data-fiscal-mode={key || "UNKNOWN"}
      title={title || (key === "LIVE" ? "Filed to URA production" : key === "TEST" ? "URA sandbox: not a real tax document" : "Environment not reported")}
      style={{
        background: tone.bg,
        color: tone.fg,
        ...(size === "md" ? TYPE.secondary : TYPE.caption),
        padding: size === "md" ? "4px 12px" : "2px 8px",
        fontWeight: 700,
        letterSpacing: "0.06em",
      }}
    >
      {text}
    </span>
  );
};

/**
 * A fiscal document number (or an application reference, when `label` says
 * so) with its environment printed beside it. Never render an FDN any other
 * way.
 *
 * @param {{ value?: string|null, mode: string, emptyText?: string, mono?: boolean }} props
 */
export const FiscalNumber = ({ value, mode, emptyText = "Not issued", mono = true }) => {
  const text = value ? String(value) : null;
  return (
    <span className="lk-fiscal-number" data-fiscal-number={text || ""} style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
      {text ? (
        <span style={mono ? TYPE.code : undefined}>{text}</span>
      ) : (
        <span style={{ color: "#8595a8" }}>{emptyText}</span>
      )}
      <FiscalModeBadge mode={mode} />
    </span>
  );
};

export default FiscalModeBadge;
